import tradeSettingsStore from "@/store/tradeSettingsStore";
import agGridFlashNotifications from "@/store/notifications/agGridFlashNotifications";
import { ICPSettingsOverride } from "../../helper/tradeSettingsHelper/tradeSettingsInterfaces";

// LQC-1040 — Iceberg-specific helpers for the Edit Panel CP settings grid.
// Lives alongside CPSettingsGridHelper.tsx (general grid helpers) so the
// iceberg rules (floor lookup, grey-out at 100%, clear-button gate, flash
// trigger) stay grouped without bloating the parent component.

/**
 * Tooltip shown on iceberg cells that have been greyed out at 100% post-send
 * (any further movement would be a forbidden reduction).
 */
export const ICEBERG_AT_MAX_TOOLTIP =
  "Iceberg is at 100% — cannot be reduced once trade is sent";

/**
 * Triggers the iceberg-cell flash on the Edit Panel and auto-clears after the
 * standard duration. cpCode undefined targets the trade-level row.
 */
export function flashIcebergCpCell(esid: string, cpCode?: string) {
  tradeSettingsStore
    .getState()
    .updateIcebergCpCellsToFlash([{ esid, cpCode, field: "releaseSize" }]);
  setTimeout(
    () => tradeSettingsStore.getState().clearIcebergCpCellsToFlash(),
    agGridFlashNotifications.getState().validationFlashDuration,
  );
}

/**
 * Trade-level iceberg cell is greyed out at 100% post-send. We don't track a
 * trade-level baseline (it resets to defaults on hydration), so the test is
 * just "current value === 100 in edit mode". Per-CP rows use a different rule
 * keyed off the per-CP baseline — see CPSettingsGrid for that branch.
 */
export function isTradeLevelIcebergLockedAt100(
  value: number | string | undefined,
  editBaseline: Record<string, ICPSettingsOverride> | undefined,
): boolean {
  if (!editBaseline) return false; // New trade — no post-send rule.
  return Number(value) === 100;
}

/**
 * The implicit per-CP iceberg floor when no explicit baseline entry exists
 * for that CP. CPs whose API value matched the trade-level default (and CPs
 * with no API releaseSize at all) have no entry in editBaselineOverrides;
 * the bank has only ever seen the trade-level default, which is hardcoded to
 * 100% — so the floor for those CPs is 100. Kept as a named constant so the
 * relationship is explicit at every call site.
 */
const IMPLICIT_CP_ICEBERG_FLOOR = 100;

/**
 * Returns the explicit per-CP iceberg baseline — only set when the API
 * shipped a per-CP releaseSize that diverged from the trade-level default.
 * Use {@link getEffectiveIcebergFloorForCP} for floor enforcement; this
 * helper is exposed for callers that need to distinguish "no explicit
 * baseline" from "explicit baseline at the implicit floor."
 */
export function getIcebergBaselineForCP(
  editBaseline: Record<string, ICPSettingsOverride> | undefined,
  cpCode: string,
): number | undefined {
  if (!editBaseline) return undefined;
  const raw = editBaseline[cpCode]?.releaseSize;
  if (raw === undefined || raw === null) return undefined;
  const num = Number(raw);
  return isNaN(num) ? undefined : num;
}

/**
 * Returns the effective iceberg floor for a CP — the highest value the bank
 * has seen for that CP. Falls back to {@link IMPLICIT_CP_ICEBERG_FLOOR} when
 * no explicit baseline entry exists, because CPs without an explicit
 * releaseSize were sent at the trade-level default.
 *
 * Returns undefined when:
 *   - we're on a new trade (no editBaseline at all), OR
 *   - the CP isn't in the API-known set: a CP added during the edit session
 *     that never received the trade has no floor and can be set anywhere
 *     1–100.
 */
export function getEffectiveIcebergFloorForCP(
  editBaseline: Record<string, ICPSettingsOverride> | undefined,
  cpCode: string,
  isApiKnownCp: boolean,
): number | undefined {
  if (!editBaseline) return undefined; // new trade
  if (!isApiKnownCp) return undefined; // brand-new CP added in edit session
  return (
    getIcebergBaselineForCP(editBaseline, cpCode) ?? IMPLICIT_CP_ICEBERG_FLOOR
  );
}

